/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { Formik, useFormikContext } from 'formik';
import UserUtil from "../../../utilities/user-util";
import { RadioGroupItem } from "../../radio-group/radio-group-item";
import { PlanFields } from "./plan-fields";
import { PlanType } from "../../../models/enumerations/plan-type";
import { DefaultFields } from "./default-fields";
import { OrganizationType, OrganizationTypeLabel } from "../../../models/enumerations/organization-type";
import { OrganizationSubscription } from "../../../models/interfaces/organization-subscription";
import { Organization } from "../../../models/interfaces/organization";
import { useAuthState } from "../../../utilities/contexts/auth-state-context";
import OrganizationService from "../../../utilities/services/organization-service";
import OrganizationSubscriptionService from "../../../utilities/services/organization-subscription-service";
import moment, { Moment } from "moment";
import { OrganizationFormValues, PartialOrgFormValues, TrialOrgFormValues } from "./form-values";
import EditOrganizationValidationRules from "./edit-organization-validation";
import { User } from "../../../models/interfaces/user";
import { UserStatus } from "../../../models/enumerations/user-status";
import UserService from "../../../utilities/services/user-service";
import { useHistory } from "react-router";
import UserRoles from "../../../models/user-roles";
import OrganizationUtil from "../../../utilities/organization-util";
import { Switch, Checkbox, FormControlLabel, Grid } from '@mui/material';
import { OrganizationStatusBadge } from "../../organization-status-badge/organization-status-badge";
import { createLogger } from "../../../utilities/logger";
import { OrganizationStatus } from "../../../models/enumerations/organization-status";
import { PaymentTypeOptions } from "../../../models/payment-type";
import UniqueUserInviteLinksService from "../../../utilities/services/unique-user-invite-link-service";
import { UniqueUserInviteLink } from "../../../models/interfaces/unique-user-invite-link";
import { PaymentType } from "../../../models/enumerations/payment-type";
import { Timestamp } from "firebase/firestore";
import { enqueueSnackbar } from "notistack";
import { AppContentType } from "../../../models/enumerations/app-content-type";
import { AppContent } from "../../../models/interfaces/app-content";
//import FunctionService from "../../../utilities/services/function-service";
import { SymmioAccessType } from "../../../models/enumerations/symmio-access-type";
import { Button } from "../../button/button";
import { useTranslation } from 'react-i18next';
import { PlanTypeLabels } from "../../../models/plan-type";
import { UserLimitPlanType } from "../../../models/enumerations/user-limit";
import { LicenseLimitPlanType } from "../../../models/enumerations/license-limit";
import BillingService from "../../../utilities/services/billing-service";
import ExternalOrgService from "../../../utilities/services/external-org-service";
import { normalizeEmail } from "../../../utilities/email-util";

const log = createLogger("EditOrganizationForm");

/**
 * Surfaces validation failures that occur on an otherwise silent submit. Some
 * validated fields (e.g. startDate) are never rendered, so an invalid form
 * previously appeared to "do nothing" when the user clicked save. Only those
 * invisible-field errors are toasted; visible fields already show inline errors.
 */
const FormValidationErrorReporter: React.FC = () => {
    const { t } = useTranslation();
    const formik = useFormikContext<any>();
    const { submitCount, errors } = formik;

    useEffect(() => {
        const errorKeys = Object.keys(errors ?? {});

        if (submitCount > 0 && errorKeys.length > 0) {
            const getFieldEl = (key: string) => document.querySelector<HTMLElement>(
                `[name="${key}"], [id="${key}"], label[for="${key}"]`
            );

            const isVisible = (key: string) => {
                const el = getFieldEl(key);
                return !!el && el.getClientRects().length > 0;
            };

            const invisibleKeys = errorKeys.filter((key) => !isVisible(key));

            if (invisibleKeys.length > 0) {
                const firstKey = invisibleKeys[0];
                const message = errors[firstKey]?.toString();
                const labelKey = `organization.edit.fields.${firstKey}`;
                const label = t(labelKey);
                const firstText = label && label !== labelKey ? `${label}: ${message}` : message;
                const more = invisibleKeys.length > 1 ? ` (+${invisibleKeys.length - 1} more)` : '';
                enqueueSnackbar(`${firstText}${more}`, { variant: "error" });
            }

            const firstVisibleKey = errorKeys.find(isVisible);

            if (firstVisibleKey) {
                const fieldEl = getFieldEl(firstVisibleKey);

                if (fieldEl) {
                    fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitCount, errors]);

    return null;
};


type EditOrganizationFormProps = {
    organizationId?: string,
    redirect?: string,
    onSave?: Function,
    onCancel?: Function,
};

const accountTypeRadioItems: RadioGroupItem[] = [
    {
        value: OrganizationType.Paid,
        label: OrganizationTypeLabel[OrganizationType.Paid],
    },
    {
        value: OrganizationType.Trial,
        label: OrganizationTypeLabel[OrganizationType.Trial],
    },
];

export const EditOrganizationForm: React.FC<EditOrganizationFormProps> = (props: EditOrganizationFormProps) => {
    const { organizationId, redirect, onSave, onCancel } = props;
    const { state } = useAuthState();
    const history = useHistory();
    const user = state.user;
    const isAccountHolder = UserUtil.isAccountHolder(user);

    const [accountHolderName, setAccountHolderName] = useState<string>();
    const [accountType, setAccountType] = useState<RadioGroupItem>(accountTypeRadioItems[0]);
    const [active, setActive] = useState<boolean>(true);
    const [expired, setExpired] = useState<boolean>();
    const [initCustomTrialLength, setInitCustomTrialLength] = useState<boolean>(false);
    const [initialForm, setInitialForm] = useState<OrganizationFormValues | PartialOrgFormValues | TrialOrgFormValues | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [organization, setOrganization] = useState<Organization>();
    const [, setPlanType] = useState<PlanType | null>(null);
    const [refresh, setRefresh] = useState<boolean>(false);
    const [, setStartDate] = useState<string>();
    const [subscriptionId, setSubscriptionId] = useState<string>();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [testOrg, setTestOrg] = useState<boolean>(state.user ? UserUtil.isDeveloperUser(state.user) : false);
    const [externalApiKeyMasked, setExternalApiKeyMasked] = useState<string>("");
    const [externalApiKeyLoading, setExternalApiKeyLoading] = useState<boolean>(false);
    const [rotateConfirmVisible, setRotateConfirmVisible] = useState<boolean>(false);
    const [, setUserCount] = useState<number | string>('');
    const [, setUserLimit] = useState<number | string>('');
    const [newFormTimeout, setNewFormTimeout] = useState<boolean>(true);
    const formikRef = useRef<any>(null);
    const { t } = useTranslation();
    setTimeout(() => setNewFormTimeout(false), 100)
    // #region UseEffects

    const getHolderName = (holder: User) => {
        let name = "";
        if (holder.firstName || holder.lastName) {
            if (holder.firstName) {
                name += holder.firstName;
            }
            if (holder.lastName) {
                if (holder.firstName) name += ' ';
                name += holder.lastName;
            }
        } else if (holder.email) {
            name += holder.email;
        }

        // if we get this far and there's no useable name, we'll just use the
        // id, but there should always be at least an email
        if (name === "" && holder.id) {
            return `id: ${holder.id}`
        }

        return name;
    };
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            if (organizationId) {
                // get the organization being edited
                const o = await OrganizationService.get(organizationId);

                const temp = await OrganizationSubscriptionService.getByOrganizationId(organizationId);
                const orgSub = temp[0];

                let accHolderName = '';

                if (o && o.id) {
                    setOrganization(o);

                    setActive(o.status === OrganizationStatus.Active);

                    if (o.testingOrg === true) {
                        setTestOrg(true);
                    } else {
                        setTestOrg(false);
                    }

                    if (orgSub.accountType.value === OrganizationType.Trial) {
                        // trial account type
                        setAccountType(accountTypeRadioItems[1]);
                    } else {
                        // paid account type
                        setAccountType(accountTypeRadioItems[0]);
                    }

                    const uCount: number = await OrganizationUtil.getOrganizationUserCount(o, true);
                    setUserCount(uCount ?? '??');
                    setUserLimit(o.userLimit ?? 'no limit');

                    if (o.accountHolderId) {
                        const holder = await UserService.get(o.accountHolderId);
                        if (holder) {
                            const holderName = getHolderName(holder);
                            setAccountHolderName(holderName);
                            accHolderName = holderName;
                        } else {
                            setAccountHolderName('n/a');
                            accHolderName = 'n/a';
                        }
                    } else {
                        setAccountHolderName('n/a');
                        accHolderName = 'n/a';
                    }

                    if (orgSub.accountType.value === OrganizationType.Paid) {
                        // try to get the organization subscription
                        const subRes = await OrganizationSubscriptionService.getByOrganizationId(o.id);

                        if (subRes && subRes.length > 0 && subRes[0].id) {
                            const sub: OrganizationSubscription = subRes[0];
                            setSubscriptionId(sub.id);
                            setPlanType(sub?.plan?.value!);
                            let userReduction = 0;
                            let wellnessReduction = 0;
                            let additionalUserLimit = 0
                            let additionalWellnessLimit = 0;
                            if (o.plan && o.plan.value && (o.plan.value === PlanType.Core || o.plan.value === PlanType.Pro)) {
                                userReduction = UserLimitPlanType[o.plan.value] || 0;
                                wellnessReduction = LicenseLimitPlanType[o.plan.value] || 0;
                                additionalUserLimit = orgSub.userLimit - userReduction;
                                additionalWellnessLimit = orgSub.mySymmioLicense ? orgSub.mySymmioLicense - wellnessReduction : 0;
                            }
                            // set initial form values
                            const init: OrganizationFormValues = {
                                accountType: sub.accountType,
                                organizationName: o.name ?? '',
                                accountHolderName: accHolderName,
                                plan: { value: sub.plan?.value!, label: PlanTypeLabels[sub.plan?.value! as PlanType] },
                                paymentCycle: sub.paymentCycle!,
                                userLimit: sub.userLimit,
                                startDate: sub.startDate && moment(sub.startDate).isValid() ? moment(sub.startDate) : moment(),
                                renewDate: sub.renewDate && moment(sub.renewDate).isValid() ? moment(sub.renewDate) : moment().add(1, 'month'),
                                availableOrganizations: sub.organizationLimit,
                                mySymmioLicense: sub.mySymmioLicense,
                                subscriptionName: sub.subscriptionName ?? '',
                                mySymmioYearlyPrice: sub.mySymmioYearlyPrice ?? 0,
                                mySymmioMonthlyPrice: sub.mySymmioMonthlyPrice ?? 0,
                                billingStartDate: sub.billingStartDate ? moment(sub.billingStartDate) : undefined,
                                accountManager: o.accountManager,
                                industry: o.industry,
                                webhookUrl: o.webhookUrl,
                                additionalUserLimit: additionalUserLimit,
                                additionalWellnessLimit: additionalWellnessLimit,
                            }
                            if (sub.paymentType) init.paymentType = sub.paymentType;
                            if (sub.price || sub.price === 0) init.price = sub.price;
                            if (sub.pricePerUser) init.pricePerUser = sub.pricePerUser;

                            const exp = moment(sub.renewDate).isSameOrBefore(moment());
                            setExpired(exp);

                            setInitialForm(init);
                        } else {
                            // this will be the case for organizations created or
                            // last edited before this new organization creation
                            // form was made
                            let accType = accountTypeRadioItems[0];
                            const temp = await OrganizationSubscriptionService.getByOrganizationId(o.id);
                            const orgSub = temp[0];
                            if (orgSub.accountType.value === OrganizationType.Trial) {
                                accType = accountTypeRadioItems[1];
                            }

                            setActive(o.status !== OrganizationStatus.Deactivated)

                            if (typeof o.status === 'string') {
                                if (orgSub.accountType.value === OrganizationType.Trial) {
                                    accType = accountTypeRadioItems[1];
                                }

                                setActive(o.status !== OrganizationStatus.Deactivated.toString())
                            }

                            let startDate = moment();
                            if (o.created) {
                                const timestamp = o.created as Timestamp;
                                if (timestamp) {
                                    startDate = moment(timestamp.seconds, 'X');
                                }
                            }

                            setInitialForm({
                                accountType: accType,
                                organizationName: o.name ?? '',
                                accountHolderName: accHolderName ?? '',
                                startDate,
                            });
                        }
                        // this will be the case for trial organizations
                    } else {
                        if (o.accountHolderId) {
                            const holder = await UserService.get(o.accountHolderId);
                            if (holder) {
                                const holderName = getHolderName(holder);
                                setAccountHolderName(holderName);
                                accHolderName = holderName;
                            } else {
                                setAccountHolderName('n/a');
                                accHolderName = 'n/a';
                            }
                        } else {
                            setAccountHolderName('n/a');
                            accHolderName = 'n/a';
                        }

                        const orgSubRes = await OrganizationSubscriptionService.getByOrganizationId(o.id);

                        // this is the case for new trial organizations
                        if (orgSubRes && orgSubRes.length > 0) {
                            setSubscriptionId(orgSubRes[0].id);
                            const orgSub = orgSubRes[0];
                            const startDate = orgSub.startDate && moment(orgSub.startDate).isValid() ? moment(orgSub.startDate) : moment();
                            const expDate = orgSub.renewDate && moment(orgSub.renewDate).isValid() ? moment(orgSub.renewDate) : moment().add(1, 'month');
                            const trialLength = orgSub.trialLength;
                            setStartDate(startDate.format('MM/DD/yyyy'));
                            let userReduction = 0;
                            let wellnessReduction = 0;
                            let additionalUserLimit = 0
                            let additionalWellnessLimit = 0;
                            if (o.plan && o.plan.value && o.plan.value === PlanType.Trial) {
                                userReduction = UserLimitPlanType[o.plan.value] || 0;
                                wellnessReduction = LicenseLimitPlanType[o.plan.value] || 0;
                                additionalUserLimit = orgSub.userLimit - userReduction;
                                additionalWellnessLimit = orgSub.mySymmioLicense ? orgSub.mySymmioLicense - wellnessReduction : 0;
                            }

                            const init: OrganizationFormValues = {
                                accountType: orgSub.accountType,
                                organizationName: o.name ?? '',
                                accountHolderName: accHolderName,
                                userLimit: orgSub.userLimit,
                                mySymmioLicense: orgSub.mySymmioLicense,
                                availableOrganizations: orgSub.organizationLimit,
                                price: 0,
                                startDate,
                                renewDate: expDate,
                                trialLength: trialLength,
                                accountManager: o.accountManager,
                                industry: o.industry,
                                webhookUrl: o.webhookUrl,
                                plan: o.plan,
                                additionalUserLimit: additionalUserLimit,
                                additionalWellnessLimit: additionalWellnessLimit
                            }

                            if (trialLength && trialLength.value === 0) {
                                init.customTrialLength = orgSub.customTrialLength;
                                setInitCustomTrialLength(true);
                            }

                            const isExpired = expDate.isSameOrBefore(moment());
                            setExpired(isExpired);

                            setInitialForm(init);
                            // this is the case for old trial organizations
                        } else {
                            const expDate = o.expiration && moment(o.expiration).isValid() ? moment(o.expiration) : moment().add(1, 'month');

                            const isExpired = expDate.isSameOrBefore(moment());
                            setExpired(isExpired);

                            const init: TrialOrgFormValues = {
                                accountType: accountTypeRadioItems[0],
                                organizationName: o.name ?? '',
                                accountHolderName: accHolderName,
                                userLimit: o.userLimit ?? 1,
                                price: 0,
                                renewDate: moment(o.expiration),
                                availableOrganizations: o.organizationLimit ?? 1,
                                accountManager: o.accountManager,
                                industry: o.industry,
                                webhookUrl: o.webhookUrl,
                            }

                            setInitialForm(init);
                        }
                    }

                }
            }
            setLoading(false);
        }

        load();
    }, [organizationId, refresh]);

    useEffect(() => {
        const loadApiKey = async () => {
            if (!organizationId || !organization) return;
            if (organization.plan?.value !== PlanType.External) return;
            setExternalApiKeyLoading(true);
            try {
                const keyInfo = await ExternalOrgService.getKey(organizationId);
                if (keyInfo?.maskedKey) {
                    setExternalApiKeyMasked(keyInfo.maskedKey);
                }
            } catch {
                // Key may not exist yet
                log.error("Failed to load external API key", {
                    organizationId,
                });
            }
            setExternalApiKeyLoading(false);
        };
        loadApiKey();
    }, [organizationId, organization]);

    useEffect(() => {
        setRefresh(true);
        setRefresh(false);
    }, [])
    // #endregion UseEffects

    // #region Handlers

    const getOrganizationStatus = () => {
        if (!organization) {
            return OrganizationStatus.Active;
        }

        const status = organization.status;

        if (typeof status === 'string') {
            if (status === OrganizationStatus.Deactivated.toString() || organization.active === false) {
                return OrganizationStatus.Deactivated;
            }
            if (status === OrganizationStatus.Active.toString()) {
                return OrganizationStatus.Active;
            }
            if (status === OrganizationStatus.Expired.toString()) {
                return OrganizationStatus.Expired;
            }
        } else {
            if (status === OrganizationStatus.Deactivated || organization.active === false) {
                return OrganizationStatus.Deactivated
            }
            if (status === OrganizationStatus.Active) {
                return OrganizationStatus.Active;
            }
            if (status === OrganizationStatus.Expired) {
                return OrganizationStatus.Expired;
            }
        }

        return OrganizationStatus.Active;
    }

    const getValidation = () => {
        // since we declare accountTypeRadioItems in different components,
        // simple comparison doesn't work due to objects having different
        // reference in memory. We must use .value
        if (accountType.value === accountTypeRadioItems[0].value) {
            return EditOrganizationValidationRules.PaidOrganizationValidationRules();
        }

        return EditOrganizationValidationRules.TrialOrganizationValidationRules();
    };

    const handleRotateSubmit = async () => {
        try {
            const result = await ExternalOrgService.rotateKey(organizationId!);
            if (result?.maskedKey) {
                setExternalApiKeyMasked(result.maskedKey);
                enqueueSnackbar(t('organization.edit.fields.keyRotated'), { variant: "toast", width: "450px", autoHideDuration: 5000 });
            }
        } catch {
            enqueueSnackbar(t('organization.edit.fields.keyRotateFailed'), { variant: "toast", width: "450px", autoHideDuration: 5000 });
        }
    };

    const handleCopyApiKey = async () => {
        try {
            const response = await ExternalOrgService.revealKey(organizationId!);
            if (response?.key) {
                await navigator.clipboard.writeText(response.key);
                enqueueSnackbar(t('organization.edit.fields.keyCopied'), { variant: "toast", width: "450px", autoHideDuration: 5000 });
            }
        } catch {
            enqueueSnackbar(t('organization.edit.fields.keyCopyFailed'), { variant: "toast", width: "450px", autoHideDuration: 5000 });
        }
    };

    // #endregion Handlers
    // const saveOrganization = async (values: OrganizationFormValues) => {
    //     console.log("values", values);
    // }

    const saveOrganization = async (values: OrganizationFormValues) => {
        setSubmitting(true);
        try {
            if (await OrganizationService.nameExists(values.organizationName, organization?.id)) {
                formikRef.current?.setErrors({ organizationName: t('pricing.organizationPage.err_alreadyExists') });
                setSubmitting(false);
                return;
            }

            // Sub-organizations are an Enterprise / Enterprise Trial feature:
            // block moving the head to a plan that cannot hold them until the
            // sub-organizations are deleted.
            const subOrganizationCount = organization?.subOrganizationIds?.length ?? 0;
            const newPlan = values.plan?.value;

            if (organization && subOrganizationCount > 0 && !OrganizationUtil.planSupportsSubOrganizations(newPlan)) {
                enqueueSnackbar(t('organization.edit.err_planChangeWithSubOrgs'), { variant: "error" });
                log.warn("Organization plan change blocked: sub-organizations exist", {
                    organizationId: organization.id,
                    previousPlan: organization.plan?.value,
                    newPlan,
                    subOrganizationCount,
                });
                setSubmitting(false);
                return;
            }
            let org: Organization;
            // if an org is being edited, begin with its existing data
            if (organization) {
                org = { ...organization }

                // overwrite any data changed by the form
                if (!org.appContent) org.appContent = {
                    [AppContentType.PAIN]: {} as AppContent,
                    [AppContentType.BEHAVIORAL_HEALTH]: {} as AppContent,
                }
                org.name = values.organizationName.trim();
                org.userLimit = Number(values.userLimit);
                org.active = active;
                org.mySymmioLicense = Number(values.mySymmioLicense);
                org.plan = values.plan;
                org.status = active ? OrganizationStatus.Active : OrganizationStatus.Deactivated;
                org.accountManager = values.accountManager;
                org.industry = values.industry;
                org.webhookUrl = values.webhookUrl?.trim();
            } else {
                org = {
                    appContent: {
                        [AppContentType.PAIN]: {} as AppContent,
                        [AppContentType.BEHAVIORAL_HEALTH]: {} as AppContent,
                    },
                    status: OrganizationStatus.Active,
                    name: values.organizationName.trim(),
                    userLimit: Number(values.userLimit),
                    mySymmioLicense: Number(values.mySymmioLicense),
                    accountManager: values.accountManager,
                    industry: values.industry,
                    webhookUrl: values.webhookUrl?.trim(),
                };
                org.plan = values.plan;
            }
            const plan = values.plan?.value;

            if (plan === PlanType.External) {
                org.webhookUrl = "";
            }

            if (plan) {
                if (org.userLimit !== undefined && UserLimitPlanType[plan] !== undefined) {
                    org.userLimit = UserLimitPlanType[plan] + Number(values.additionalUserLimit ?? 0);
                }
                if (org.mySymmioLicense !== undefined && LicenseLimitPlanType[plan] !== undefined) {
                    org.mySymmioLicense = LicenseLimitPlanType[plan] + Number(values.additionalWellnessLimit ?? 0);
                }
            }
            if (values.plan?.value === PlanType.Enterprise || values.plan?.value === PlanType.EnterpriseTrial) {
                org.organizationLimit = Number(values.availableOrganizations);
            } else {
                org.organizationLimit = 1;
            }

            if (testOrg === true) {
                org.testingOrg = true;
            }

            // if an account holder is creating this organization, set the parent
            // org id
            if (isAccountHolder && user?.organizationId) {
                org.accountHolderId = user.organizationId;
            }

            // If this head organization has sub-organizations, ensure the new
            // limits still cover the slices allocated to those sub-organizations
            // plus the head's own usage, then store the head's remaining capacity
            // after carving those slices out.
            if (organization && (organization.subOrganizationIds?.length ?? 0) > 0) {
                const subOrgs = await OrganizationService.getBy([{
                    field: 'parentOrganizationId',
                    operator: '==',
                    value: organization.id,
                }]);

                const allocatedUsers = subOrgs.reduce((sum, s) => sum + Number(s.userLimit ?? 0), 0);
                const allocatedLicenses = subOrgs.reduce((sum, s) => sum + Number(s.mySymmioLicense ?? 0), 0);
                const headUserCount = await OrganizationUtil.getOrganizationUserCount(organization);

                const newUserTotal = Number(org.userLimit ?? 0);
                const newLicenseTotal = Number(org.mySymmioLicense ?? 0);

                if (newUserTotal < allocatedUsers + headUserCount) {
                    formikRef.current?.setErrors({ userLimit: t('organization.edit.err_allocationExceedsHead') });
                    log.warn("Organization update blocked: user limit below sub-organization allocations", {
                        organizationId: organization.id,
                        userLimit: newUserTotal,
                        allocatedUsers,
                        headUserCount,
                    });
                    setSubmitting(false);
                    return;
                }

                if (newLicenseTotal < allocatedLicenses + (organization.mySymmioLicenseCount ?? 0)) {
                    formikRef.current?.setErrors({ mySymmioLicense: t('organization.edit.err_allocationExceedsHead') });
                    log.warn("Organization update blocked: license limit below sub-organization allocations", {
                        organizationId: organization.id,
                        licenseLimit: newLicenseTotal,
                        allocatedLicenses,
                        licenseCount: organization.mySymmioLicenseCount ?? 0,
                    });
                    setSubmitting(false);
                    return;
                }

                org.userLimit = newUserTotal - allocatedUsers;
                org.mySymmioLicense = newLicenseTotal - allocatedLicenses;
            }

            // Audit logging for admin-form org changes (status diffs especially)
            if (organization) {
                const changedFields: Record<string, { from: unknown; to: unknown }> = {};
                const track = (key: string, from: unknown, to: unknown) => {
                    if (JSON.stringify(from) !== JSON.stringify(to)) {
                        changedFields[key] = { from, to };
                    }
                };

                track("name", organization.name, org.name);
                track("status", organization.status, org.status);
                track("active", organization.active, org.active);
                track("plan", organization.plan, org.plan);
                track("userLimit", organization.userLimit, org.userLimit);
                track("mySymmioLicense", organization.mySymmioLicense, org.mySymmioLicense);
                track("accountManager", organization.accountManager, org.accountManager);
                track("industry", organization.industry, org.industry);
                track("webhookUrl", organization.webhookUrl, org.webhookUrl);
                track("testingOrg", organization.testingOrg, org.testingOrg);

                if (organization.status !== org.status) {
                    log.warn("Organization status changed", {
                        organizationId: org.id ?? organizationId,
                        organizationName: org.name,
                        fromStatus: OrganizationStatus[Number(organization.status)] ?? organization.status,
                        fromStatusValue: organization.status,
                        toStatus: OrganizationStatus[Number(org.status)] ?? org.status,
                        toStatusValue: org.status,
                        active,
                        changedBy: user?.id,
                        changedByEmail: user?.email,
                        source: "admin-org-form",
                    });
                }

                if (Object.keys(changedFields).length > 0) {
                    log.info("Organization updated", {
                        organizationId: org.id ?? organizationId,
                        organizationName: org.name,
                        changedFields,
                        changedBy: user?.id,
                        changedByEmail: user?.email,
                        source: "admin-org-form",
                    });
                }
            } else {
                log.info("Organization created", {
                    organizationId: org.id ?? organizationId,
                    organizationName: org.name,
                    status: OrganizationStatus[Number(org.status)] ?? org.status,
                    statusValue: org.status,
                    plan: org.plan?.value,
                    changedBy: user?.id,
                    changedByEmail: user?.email,
                    source: "admin-org-form",
                });
            }

            let savedOrg = await OrganizationService.save(org, user);
            setOrganization(savedOrg);

            let holderExists = false;

            if (values.accountHolderEmail) {
                const oldHolder = await UserService.getBy([{
                    field: 'email',
                    operator: '==',
                    value: normalizeEmail(values.accountHolderEmail),
                }]);

                holderExists = oldHolder.length > 0;
            }

            // if creating a new organization, we need to create the account holder
            if (plan !== PlanType.External && savedOrg && savedOrg.id && !organization && !holderExists) {
                const accHolder: User = {
                    email: values.accountHolderEmail?.toLowerCase(),
                    status: UserStatus.Active,
                    isSuperAdmin: false,
                    organizationId: savedOrg.id,
                    roles: [UserRoles.ACCOUNT_HOLDER_ID],
                    symmioAccess: SymmioAccessType.AppLicense,
                    notificationSettings: {
                        inactiveUser: true,
                        highRiskUser: true,
                        dayStreakMilestone: true,
                        newAccount: true,
                        completedTasks: true,
                        baselineAssessment: true,
                    },
                    emailNotificationSettings: {
                        baselineAssessment: false,
                        highRiskUser: false,
                        startedProgram: false,
                        completedProgram: false,
                    }
                };

                const savedHolder = await UserService.add(accHolder, user);

                // we now need to save the org again to apply the account holder id
                if (savedHolder && savedHolder.id && values.renewDate) {
                    const uniqueInviteLink: UniqueUserInviteLink = {
                        userId: savedHolder.id,
                        senderEmail: state.user?.email,
                        organizationId: state.organization.id,
                    };

                    await UniqueUserInviteLinksService.save(uniqueInviteLink, state.user);

                    savedOrg.accountHolderId = savedHolder.id;
                    savedOrg = await OrganizationService.save(savedOrg, user);
                    setOrganization(savedOrg);
                }
            }

            if (plan !== PlanType.External && savedOrg && accountHolderName === 'n/a' && values.accountHolderId && values.accountHolderId.value !== 'nothingOption') {
                const hUser = await UserService.get(values.accountHolderId.value);
                if (hUser) {
                    hUser.roles = [UserRoles.ACCOUNT_HOLDER_ID];
                    await UserService.save(hUser);
                    savedOrg.accountHolderId = values.accountHolderId.value;
                    savedOrg = await OrganizationService.save(savedOrg);
                }
            }


            if (savedOrg && savedOrg.id) {
                let startDate: string | Moment = values.startDate as Moment;
                startDate = startDate.toISOString();
                const orgSub: OrganizationSubscription = {
                    organizationId: savedOrg.id,
                    accountType: values.accountType,
                    plan: values.plan,
                    paymentCycle: (values.plan?.value === PlanType.Enterprise || values.plan?.value === PlanType.External) ? values.paymentCycle : undefined,
                    price: 0,
                    userLimit: Number(values.userLimit),
                    startDate: startDate,
                    renewDate: values.plan?.value !== PlanType.ThirdParty && values.plan?.value !== PlanType.External
                        ? (values.renewDate as Moment).toISOString()
                        : undefined,
                    mySymmioLicense: Number(values.mySymmioLicense),
                    organizationLimit: 1,
                };
                if (plan) {
                    if (orgSub.userLimit !== undefined && UserLimitPlanType[plan] !== undefined) {
                        orgSub.userLimit = UserLimitPlanType[plan] + Number(values.additionalUserLimit ?? 0);
                    }
                    if (orgSub.mySymmioLicense !== undefined && LicenseLimitPlanType[plan] !== undefined) {
                        orgSub.mySymmioLicense = LicenseLimitPlanType[plan] + Number(values.additionalWellnessLimit ?? 0);
                    }
                }
                if (orgSub.plan && orgSub.plan.value === PlanType.Enterprise) {
                    orgSub.paymentType = values.paymentType;

                    if (values.paymentType?.value === PaymentType.FlatFee) {
                        orgSub.price = values.price;
                    } else {
                        orgSub.pricePerUser = values.pricePerUser;
                    }
                    if (subscriptionId) {
                        orgSub.id = subscriptionId;
                    }
                    orgSub.organizationLimit = Number(values.availableOrganizations ?? 1);
                    await OrganizationSubscriptionService.save(orgSub, user);

                } else if (orgSub.plan && orgSub.plan.value === PlanType.Business) {
                    orgSub.price = values.price;
                } else if (orgSub.plan && orgSub.plan.value === PlanType.ThirdParty && values.subscriptionName && values.mySymmioMonthlyPrice !== undefined && values.mySymmioYearlyPrice !== undefined) {
                    // Make sure we're not creating a new product and prices again on edit
                    if (!orgSub.thirdPartyProductId && !orgSub.mySymmioYearlyPriceId && !orgSub.mySymmioMonthlyPriceId) {
                        orgSub.subscriptionName = values.subscriptionName;
                        orgSub.mySymmioYearlyPrice = Number(values.mySymmioYearlyPrice);
                        orgSub.mySymmioMonthlyPrice = Number(values.mySymmioMonthlyPrice);

                        const productResponse = await BillingService.createThirdPartyProductAndPrices({
                            name: orgSub.subscriptionName,
                            monthlyPrice: orgSub.mySymmioMonthlyPrice,
                            yearlyPrice: orgSub.mySymmioYearlyPrice,
                        });

                        orgSub.thirdPartyProductId = productResponse.productId;
                        orgSub.mySymmioMonthlyPriceId = productResponse.monthlyPriceId;
                        orgSub.mySymmioYearlyPriceId = productResponse.yearlyPriceId;
                    }

                    if (subscriptionId) orgSub.id = subscriptionId;

                    if (testOrg) {
                        orgSub.testingSub = true;
                    }

                    await OrganizationSubscriptionService.save(orgSub, user);

                } else if (orgSub.plan && orgSub.plan.value === PlanType.External) {
                    orgSub.paymentCycle = values.paymentCycle || null;
                    orgSub.billingStartDate = values.billingStartDate ? (values.billingStartDate as Moment).toISOString() : null;
                    orgSub.renewDate = undefined;
                    if (subscriptionId) orgSub.id = subscriptionId;
                    if (testOrg) orgSub.testingSub = true;
                    await OrganizationSubscriptionService.save(orgSub, user);

                } else {
                    const expMoment = values.renewDate as Moment;
                    const expStr = expMoment.toISOString();
                    const orgSub: OrganizationSubscription = {
                        organizationId: savedOrg.id,
                        accountType: values.accountType,
                        userLimit: Number(values.userLimit),
                        organizationLimit: Number(values.availableOrganizations ?? 1),
                        startDate: moment().toISOString(),
                        renewDate: expStr,
                        mySymmioLicense: Number(values.mySymmioLicense),
                        trialLength: values.trialLength,
                        plan: values.plan,
                    }

                    if (plan) {
                        if (orgSub.userLimit !== undefined && UserLimitPlanType[plan] !== undefined) {
                            orgSub.userLimit = UserLimitPlanType[plan] + Number(values.additionalUserLimit ?? 0);
                        }
                        if (orgSub.mySymmioLicense !== undefined && LicenseLimitPlanType[plan] !== undefined) {
                            orgSub.mySymmioLicense = LicenseLimitPlanType[plan] + Number(values.additionalWellnessLimit ?? 0);
                        }
                    }

                    if (subscriptionId) {
                        orgSub.id = subscriptionId;
                    }

                    if (values.trialLength && values.trialLength.value === 0) {
                        orgSub.customTrialLength = values.customTrialLength;
                    }

                    if (testOrg) {
                        orgSub.testingSub = true;
                    }

                    await OrganizationSubscriptionService.save(orgSub, user);
                }

                setRefresh(!refresh);
                redirect && history.push(redirect);

                setOrganization(savedOrg);

                if (plan === PlanType.External && savedOrg.id) {
                    const billingDate = values.billingStartDate ? (values.billingStartDate as Moment).toISOString() : undefined;
                    if (!organization) {
                        ExternalOrgService.generateKey(savedOrg.id, savedOrg.name ?? "", values.paymentCycle, billingDate).catch(() => { });
                    }
                    ExternalOrgService.setKeyStatus(savedOrg.id, active, values.paymentCycle, billingDate).catch(() => { });
                }
            }

            // if (!organizationId && values.accountType.value === OrganizationType.Trial) {
            //     const accHolder: User = {
            //         email: values.accountHolderEmail!.toLowerCase(),
            //         status: UserStatus.Active,
            //         isSuperAdmin: false,
            //         organizationId: savedOrg.id,
            //         roles: [UserRoles.ACCOUNT_HOLDER_ID],
            //     };
            //     FunctionService.sendAccountOwnerWelcomeEmail(accHolder);
            // }
            setSubmitting(false);
            onSave && onSave();
            enqueueSnackbar(organizationId ? t('organization.edit.orgEdited') : t('organization.edit.orgCreated'), { variant: "toast", width: "450px" });
            log.info(organizationId ? "Organization updated" : "Organization created", {
                organizationId: savedOrg?.id,
                plan: values.plan?.value,
                userLimit: Number(values.userLimit),
                licenseLimit: Number(values.mySymmioLicense),
                isTestOrg: testOrg === true,
            });
        } catch (e) {
            log.error(organizationId ? "Failed to update organization" : "Failed to create organization", {
                exception: e,
                organizationId,
            });
            setSubmitting(false);
        }
    }

    return (
        <>
            <div style={{ margin: '32px 0px 32px 0px', display: 'flex' }}>
                <h1 style={{ display: 'inline', margin: '0px 40px 0px 0px' }}>{organizationId ? t('organization.edit.orgTitle') : t('organization.edit.addOrganization')}</h1>
                {
                    organization && (
                        <div style={{ display: 'inline', padding: '2px 0px 2.5px 0px' }}>
                            <OrganizationStatusBadge status={getOrganizationStatus()} expired={expired} />
                        </div>
                    )
                }
                {
                    state.user && UserUtil.isDeveloperUser(state.user)
                    && (
                        <div style={{ display: 'inline', width: 'fit-content', background: '#c1d4e0', paddingLeft: '10px', borderRadius: '4px', margin: '1px 0px 1px 60px' }}>
                            <FormControlLabel
                                sx={{ paddingTop: '2px', userSelect: 'none' }}
                                control={<Checkbox onChange={(e) => setTestOrg(e.target.checked)} checked={testOrg} size="small" sx={{ padding: '1px 10px 1px 10px' }} />}
                                label={t('organization.edit.fields.testingOrg')}
                            />
                        </div>
                    )
                }
            </div>
            <Formik
                innerRef={formikRef}
                initialValues={
                    initialForm !== null
                        ? initialForm
                        : {
                            pricePerUser: 3.00,
                            availableOrganizations: 1,
                            startDate: moment(),
                            accountType: accountTypeRadioItems[0],
                            paymentType: PaymentTypeOptions[0],
                        }
                }
                enableReinitialize={loading || newFormTimeout}
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={(loading || newFormTimeout) ? null : getValidation()}
                onSubmit={(values: any) => {
                    saveOrganization(values);
                }}
            >
                {({
                    handleSubmit,
                    isSubmitting,
                    setErrors,
                    errors
                }) => (
                    <>
                        <FormValidationErrorReporter />
                        {/* {console.log(errors)} */}
                        <form onSubmit={handleSubmit} noValidate>
                            <div>

                                {/* <Grid container rowSpacing={3} sx={{ marginBottom: '38px' }}>
                                <Grid item md={12} lg={8}>
                                    <FormikRadioGroup
                                        name="accountType"
                                        items={accountTypeRadioItems}
                                        value={accountType}
                                        onChange={(option: RadioGroupItem) => {
                                            handleAccountTypeChange(option);
                                            setErrors({});
                                        }}
                                    />
                                </Grid>
                                {
                                    organization && (
                                        <>
                                            <Grid item xs={6} md={3} lg={2}>
                                                <p style={{ fontSize: '12px', fontWeight: '700', paddingBottom: '8px' }}>{t('deeplinks.edit.startDate')}</p>
                                                <p style={{ fontSize: '16px', fontWeight: '300' }}>{startDate ?? 'n/a'}</p>
                                            </Grid>
                                            <Grid item xs={6} md={3} lg={2}>
                                                <p style={{ fontSize: '12px', fontWeight: '700', paddingBottom: '8px' }}>{t('organization.edit.fields.userQuantity')}</p>
                                                <span style={{ fontSize: '16px', fontWeight: '700' }}>{userCount}</span>
                                                <span style={{ fontSize: '16px', fontWeight: '300' }}>{` / ${userLimit}`}</span>
                                            </Grid>
                                            <Grid item xs={0} md={6} lg={0} />
                                        </>
                                    )
                                }
                            </Grid> */}

                                <Grid container columnSpacing={3}>
                                    <Grid
                                        item
                                        xs={12} sm={12} md={12} lg={6}
                                        sx={{
                                            borderRight: { xs: 'none', lg: 'solid 2px rgba(18, 22, 34, 0.05)' },
                                            paddingRight: { xs: '0px', lg: '24px' },
                                            paddingBottom: { xs: '32px', sm: '32px', md: '32px', lg: '0px' },
                                        }}
                                    >
                                        <DefaultFields
                                            initCustomTrialLength={initCustomTrialLength}
                                            organizationId={organizationId}
                                            accountHolderName={accountHolderName}
                                            loading={loading}
                                            editing={!!organization}
                                            accountType={accountType}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={12} lg={6}>
                                        <PlanFields
                                            initCustomTrialLength={initCustomTrialLength}
                                            setAccountType={setAccountType}
                                            accountType={accountType}
                                            loading={loading}
                                            editing={!!organization}
                                            organizationId={organizationId}
                                            startDateCallback={(d: Moment) => setStartDate(d.format('MM/DD/yyyy'))}
                                            externalApiKeyMasked={externalApiKeyMasked}
                                            externalApiKeyLoading={externalApiKeyLoading}
                                            onCopyKey={handleCopyApiKey}
                                            onRotateKey={() => setRotateConfirmVisible(true)}
                                            rotateConfirmVisible={rotateConfirmVisible}
                                            onRotateConfirm={() => {
                                                setRotateConfirmVisible(false);
                                                handleRotateSubmit();
                                            }}
                                            onRotateCancel={() => setRotateConfirmVisible(false)}
                                        />
                                    </Grid>
                                </Grid>

                            </div>
                            <Grid container sx={{ marginTop: '36px' }}>
                                {
                                    organizationId && (
                                        <Grid item md={12} lg={6}>
                                            <>
                                                <div
                                                    style={{
                                                        display: 'inline-block',
                                                        position: 'absolute',
                                                        top: '215px',
                                                        padding: '0 0 0 20px',
                                                    }}
                                                >
                                                </div>
                                                <div style={{ margin: '15px 0' }}>
                                                    <div style={{ display: 'inline-block', marginRight: '10px' }}>
                                                        <Switch
                                                            inputProps={{ 'aria-label': 'Active Account' }}
                                                            checked={active}
                                                            onChange={(e: any) => setActive(e.target.checked)}
                                                        />
                                                        <p style={{ display: 'inline-block', fontSize: '12px', fontWeight: '700', marginLeft: '10px' }}>
                                                            {t('organization.edit.activeAccount')}
                                                        </p>
                                                    </div>
                                                    <div style={{ display: 'block', width: '320px', marginLeft: '68px' }}>
                                                        <p style={{ fontSize: '12px', fontWeight: '300', lineHeight: '18px', color: 'rgba(18, 22, 34, 0.5)' }}>
                                                            {t('organization.edit.deactivateWarning')}
                                                        </p>
                                                    </div>
                                                </div>
                                            </>
                                        </Grid>
                                    )
                                }
                                <Grid
                                    item
                                    md={12} lg={6}
                                    sx={{
                                        margin: '40px 0',
                                        columnGap: '20px',
                                        display: 'flex',
                                        justifyContent: {
                                            md: 'left',
                                            lg: organization ? 'right' : 'left'
                                        }
                                    }}
                                >
                                    {
                                        organization &&
                                        <Button
                                            type="link"
                                            onClick={() => onCancel && onCancel()}
                                            buttonText={t('buttons.btn_cancel')}
                                            buttonType="button"
                                        />
                                    }
                                    <Button
                                        buttonText={organization ? t('buttons.btn_save') : t('organization.edit.addOrganization')}
                                        disabled={submitting || loading}
                                        buttonType="submit"
                                    />
                                </Grid>
                            </Grid>

                        </form>
                    </>
                )}
            </Formik >
        </>

    );
}